'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import Image from 'next/image';
import styles from './FeaturesCarousel.module.css';

interface ContentBlock {
  title: string;
  content: string;
  image?: string;
}

interface FeaturesCarouselProps {
  translations: {
    badge?: string;
    sectionTitle?: string;
    sectionIntro?: string;
    contentBlocks?: ContentBlock[];
    readMore?: string;
  };
  altTexts?: {
    kiosk?: Record<string, string>;
    kioskFeatures?: Record<string, string>;
  };
  imageDirectory?: string;
}

export default function FeaturesCarousel({ translations, altTexts, imageDirectory = 'kiosk-features' }: FeaturesCarouselProps) {
  const [selectedFeature, setSelectedFeature] = useState<number | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  const contentBlocks = translations.contentBlocks || [];
  const totalSlides = contentBlocks.length;

  // Close modal
  const closeModal = useCallback(() => {
    setSelectedFeature(null);
    document.body.style.overflow = '';
  }, []);

  // Open modal
  const openModal = useCallback((index: number) => {
    setSelectedFeature(index);
    document.body.style.overflow = 'hidden';
  }, []);

  // Handle ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeModal();
    };
    
    if (selectedFeature !== null) {
      window.addEventListener('keydown', handleEsc);
      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [selectedFeature, closeModal]);

  // Navigation handlers
  const scrollToSlide = useCallback((index: number) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cardWidth = container.querySelector(`.${styles.card}`)?.clientWidth || 0;
    const gap = 24; // var(--space-6)
    const scrollPosition = index * (cardWidth + gap);
    
    container.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
    
    setCurrentSlide(index);
  }, []);

  const handlePrev = useCallback(() => {
    const newIndex = Math.max(0, currentSlide - 1);
    scrollToSlide(newIndex);
  }, [currentSlide, scrollToSlide]);

  const handleNext = useCallback(() => {
    const newIndex = Math.min(totalSlides - 1, currentSlide + 1);
    scrollToSlide(newIndex);
  }, [currentSlide, totalSlides, scrollToSlide]);

  // Update current slide on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    let timeoutId: NodeJS.Timeout;
    
    const handleScroll = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const cardWidth = container.querySelector(`.${styles.card}`)?.clientWidth || 0;
        const gap = 24;
        const newIndex = Math.round(scrollLeft / (cardWidth + gap));
        setCurrentSlide(newIndex);
      }, 100);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(timeoutId);
    };
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '400px 0px 0px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(styles.visible);
        }
      });
    }, observerOptions);

    cardsRef.current.forEach((card) => {
      if (card) observer.observe(card);
    });

    return () => observer.disconnect();
  }, [contentBlocks.length]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      handlePrev();
    } else if (e.key === 'ArrowRight') {
      handleNext();
    }
  }, [handlePrev, handleNext]);

  return (
    <>
      {/* SVG Gradients for Icons */}
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="gradient-stroke" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff9966" />
            <stop offset="50%" stopColor="#ff5e62" />
            <stop offset="100%" stopColor="#ffc371" />
          </linearGradient>
        </defs>
      </svg>

      {/* Section Header */}
      {translations.sectionTitle && (
        <div className={styles.sectionHeader}>
          {translations.badge && (
            <div className={styles.sectionBadge}>{translations.badge}</div>
          )}
          <h2 className={styles.sectionTitle}>{translations.sectionTitle}</h2>
          {translations.sectionIntro && (
            <p className={styles.sectionIntro}>{translations.sectionIntro}</p>
          )}
        </div>
      )}

      {/* Carousel Container */}
      <div className={styles.carouselWrapper} onKeyDown={handleKeyDown} tabIndex={0}>
        {/* Navigation Buttons */}
        <button
          className={`${styles.navButton} ${styles.navButtonPrev}`}
          onClick={handlePrev}
          disabled={currentSlide === 0}
          aria-label="Previous slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <button
          className={`${styles.navButton} ${styles.navButtonNext}`}
          onClick={handleNext}
          disabled={currentSlide >= totalSlides - 1}
          aria-label="Next slide"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Cards Container */}
        <div 
          ref={scrollContainerRef}
          className={styles.cardsContainer}
          role="region"
          aria-label="Features carousel"
        >
          {contentBlocks.map((block, index) => (
            <div
              key={index}
              ref={(el) => { cardsRef.current[index] = el; }}
              className={`${styles.card} ${styles.scrollReveal}`}
              onClick={() => openModal(index)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openModal(index);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={`${block.title}. Click to read more`}
            >
              {/* Number Badge - always visible */}
              <div className={styles.cardBadge}>
                {String(index + 1).padStart(2, '0')}
              </div>

              {/* Image */}
              {block.image && (
                <div className={styles.cardImageWrapper}>
                  <Image
                    src={`/images/${imageDirectory}/${block.image}.webp`}
                    alt={altTexts?.kioskFeatures?.[block.image] || block.title}
                    fill
                    sizes="(max-width: 768px) 90vw, (max-width: 1024px) 45vw, 400px"
                    style={{ objectFit: 'cover' }}
                    loading="lazy"
                    quality={75}
                  />
                </div>
              )}

              {/* Content */}
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>{block.title}</h3>
                <p className={styles.cardPreview}>
                  {block.content.substring(0, 120)}...
                </p>
                <div className={styles.cardCta}>
                  <span>{translations.readMore || 'Lees meer'}</span>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M12 5l7 7-7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>

              {/* Glow Effect */}
              <div className={styles.cardGlow} />
            </div>
          ))}
        </div>

        {/* Dot Indicators */}
        <div className={styles.dotsContainer}>
          {contentBlocks.map((_, index) => (
            <button
              key={index}
              className={`${styles.dot} ${currentSlide === index ? styles.dotActive : ''}`}
              onClick={() => scrollToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={currentSlide === index}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {selectedFeature !== null && (
        <div 
          className={styles.modalOverlay}
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <div 
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              className={styles.modalClose}
              onClick={closeModal}
              aria-label="Close modal"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Modal Body */}
            <div className={styles.modalBody}>
              {/* Number Badge */}
              <div className={styles.modalBadge}>
                {String(selectedFeature + 1).padStart(2, '0')}
              </div>

              {/* Featured Image */}
              {contentBlocks[selectedFeature].image && (
                <div className={styles.modalImageWrapper}>
                  <Image
                    src={`/images/${imageDirectory}/${contentBlocks[selectedFeature].image}.webp`}
                    alt={altTexts?.kioskFeatures?.[contentBlocks[selectedFeature].image || ''] || contentBlocks[selectedFeature].title}
                    fill
                    sizes="(max-width: 768px) 90vw, 800px"
                    style={{ 
                      objectFit: 'cover',
                      objectPosition: selectedFeature === 1 ? 'left bottom' : selectedFeature === 6 ? 'center top' : 'center center'
                    }}
                    priority
                    quality={85}
                  />
                </div>
              )}

              {/* Text Content */}
              <div className={styles.modalText}>
                <h3 id="modal-title" className={styles.modalTitle}>
                  {contentBlocks[selectedFeature].title}
                </h3>
                <div className={styles.modalDescription}>
                  {contentBlocks[selectedFeature].content.split('\n\n').map((paragraph, pIndex) => (
                    <p key={pIndex}>{paragraph}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

